import type { ServicePageData } from "$lib/types/service-detail";

const editorialRetouchingMedia = {
  heroPortrait: {
    src: "/images/editorial-retouching/editorial-retouching-hero-male-leather-jacket.webp",
    alt: "Male editorial headshot in a black leather jacket with refined skin detail",
    width: 1600,
    height: 2000,
  },
  heroFashion: {
    src: "/images/editorial-retouching/editorial-retouching-hero-crystal-glam-portrait.webp",
    alt: "Beauty fashion editorial portrait with crystal styling and neon escalator lighting",
    width: 1600,
    height: 2000,
  },
  heroMonochrome: {
    src: "/images/editorial-retouching/editorial-retouching-hero-male-suit-street.webp",
    alt: "Male fashion model in a navy suit photographed on a city street",
    width: 1600,
    height: 2000,
  },
  introSkin: {
    src: "/images/editorial-retouching/editorial-retouching-intro-fashion-detail-workflow.webp",
    alt: "Editorial retouching detail board comparing skin, hair, garment, and footwear refinements",
    width: 1122,
    height: 1402,
  },
  comparisonOriginal: {
    src: "/images/editorial-retouching/editorial-retouching-comparison-female-headshot-before.webp",
    alt: "Female headshot before retouching, with visible skin texture and small blemishes",
    width: 1600,
    height: 2000,
  },
  comparisonEditorial: {
    src: "/images/editorial-retouching/editorial-retouching-comparison-female-headshot-after.webp",
    alt: "Finished female headshot retouch with balanced skin tone and natural texture",
    width: 1600,
    height: 2000,
  },
  showcaseBefore: {
    src: "/images/editorial-retouching/editorial-retouching-showcase-executive-gradient-before.webp",
    alt: "Executive headshot before editorial retouching, with a light gray background",
    width: 1600,
    height: 2000,
  },
  showcaseAfter: {
    src: "/images/editorial-retouching/editorial-retouching-showcase-executive-gradient-after.webp",
    alt: "Finished executive headshot with a dark gradient background and refined portrait detail",
    width: 1600,
    height: 2000,
  },
  fashionCampaignBefore: {
    src: "/images/editorial-retouching/editorial-retouching-fashion-campaign-winter-before.webp",
    alt: "On-model fashion editorial shot before retouching",
    width: 1334,
    height: 1668,
  },
  fashionCampaignAfter: {
    src: "/images/editorial-retouching/editorial-retouching-fashion-campaign-winter-after.webp",
    alt: "On-model fashion editorial shot after garment, skin, and tone retouching",
    width: 1334,
    height: 1668,
  },
  studioEcomBefore: {
    src: "/images/editorial-retouching/editorial-retouching-studio-lookbook-before.webp",
    alt: "Studio fashion lookbook shot before retouching",
    width: 1600,
    height: 2000,
  },
  studioEcomAfter: {
    src: "/images/editorial-retouching/editorial-retouching-studio-lookbook-after.webp",
    alt: "Studio fashion lookbook shot after backdrop cleanup, silhouette refinement, and color grading",
    width: 1600,
    height: 2000,
  },
  loungewearPantsBefore: {
    src: "/images/editorial-retouching/apparel-magnolia-lounge-loungewear-pants-0959-before.webp",
    alt: "Loungewear pants editorial lookbook capture before fabric de-wrinkling and retouching",
    width: 1500,
    height: 1875,
  },
  loungewearPantsAfter: {
    src: "/images/editorial-retouching/apparel-magnolia-lounge-loungewear-pants-0959-after.webp",
    alt: "Finished loungewear pants editorial retouch with smooth fabric drape, aligned waistband, and natural texture",
    width: 1500,
    height: 1875,
  },
} as const;

export const editorialRetouchingPage: ServicePageData = {
  slug: "editorial-retouching",
  seo: {
    title: "Editorial Photo Retouching | Studio Click House",
    description:
      "High-end editorial photo retouching by Studio Click House. Frequency separation, micro dodge and burn, natural skin pore preservation, flyaway hair cleanup, and color grading for fashion magazines.",
  },
  hero: {
    title: "Editorial",
    titleAccent: "Retouching.",
    theme: "light",
    titleWidth: "wide",
    mediaFit: "cover",
    aspectRatio: "4/5",
    description:
      "Photo retouching for fashion campaigns, beauty close-ups, and magazine covers. Manual dodge and burn and frequency separation refine each frame while keeping real skin texture and lighting character.",
    media: editorialRetouchingMedia.heroPortrait,
    supportingMedia: [
      editorialRetouchingMedia.heroFashion,
      editorialRetouchingMedia.heroMonochrome,
    ],
  },
  intro: {
    heading: "What is high-end editorial retouching?",
    paragraphs: [
      "Editorial retouching refines blemishes, flyaway hair, uneven tones, and garment details without flattening the image. Our artists use 16-bit non-destructive workflows, manual dodge and burn, and frequency separation to keep skin texture and lighting believable.",
    ],
    stages: [
      {
        label: "Frequency Separation & Blemish Cleanup",
        description:
          "We decouple high-frequency skin pores from low-frequency color tones to remove distractions without eroding texture.",
        media: editorialRetouchingMedia.introSkin,
      },
      {
        label: "Micro Dodge & Burn Light Sculpting",
        description:
          "Pixel-by-pixel luminance adjustments sculpt facial contours, highlight cheekbones, and balance skin transition areas.",
      },
      {
        label: "Color Harmonization & Mood Grading",
        description:
          "Editorial grade tones, color balance, and filmic contrast are applied in calibrated 16-bit ProPhoto/Adobe RGB color spaces.",
      },
    ],
  },
  beforeAfter: {
    heading: "Editorial portrait retouching before and after",
    description:
      "Compare the original female headshot with its finished editorial retouch, preserving natural skin texture while refining tone, light, and detail.",
    bullets: [
      "Even skin tone while preserving natural texture",
      "Refine light, color, and fine portrait detail",
      "Keep facial features and expression natural",
    ],
    beforeSrc: editorialRetouchingMedia.comparisonOriginal.src,
    beforeAlt: editorialRetouchingMedia.comparisonOriginal.alt,
    afterSrc: editorialRetouchingMedia.comparisonEditorial.src,
    afterAlt: editorialRetouchingMedia.comparisonEditorial.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    width: editorialRetouchingMedia.comparisonEditorial.width,
    height: editorialRetouchingMedia.comparisonEditorial.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "Executive Headshot Retouching",
    description:
      "See how precise skin cleanup, balanced lighting, and a refined gradient background give executive portraits a polished, natural finish.",
    bullets: [
      "Refine skin tone while keeping realistic texture",
      "Balance portrait lighting and background color",
      "Preserve natural facial features and expression",
    ],
    beforeAfter: {
      before: { ...editorialRetouchingMedia.showcaseBefore, label: "Before" },
      after: { ...editorialRetouchingMedia.showcaseAfter, label: "After" },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "editorial-retouching-fashion-comparison",
      heading: "Garment & on-model editorial retouching",
      description:
        "Remove distracting fabric creases, refine garment draping, clean flyaway hairs, and balance ambient lighting while preserving authentic skin and fabric textures.",
      bullets: [
        "Smooth fabric wrinkles and refine silhouette lines naturally",
        "Clean stray hairs and balance natural skintones",
        "Harmonize lighting, shadows, and color balance",
      ],
      beforeSrc: editorialRetouchingMedia.fashionCampaignBefore.src,
      beforeAlt: editorialRetouchingMedia.fashionCampaignBefore.alt,
      afterSrc: editorialRetouchingMedia.fashionCampaignAfter.src,
      afterAlt: editorialRetouchingMedia.fashionCampaignAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: editorialRetouchingMedia.fashionCampaignAfter.width,
      height: editorialRetouchingMedia.fashionCampaignAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "editorial-retouching-lookbook-studio-comparison",
      heading: "Studio lookbook & silhouette refinement",
      description:
        "Polish raw studio captures with seamless backdrop cleanup, precise garment shape alignment, and balanced tone curves suited for luxury e-commerce and catalog lookbooks.",
      bullets: [
        "Clean background gradients and floor cyclorama marks",
        "Align garment symmetry, hem drape, and fitting lines",
        "Maintain true-to-life textile colors and natural skin tones",
      ],
      beforeSrc: editorialRetouchingMedia.studioEcomBefore.src,
      beforeAlt: editorialRetouchingMedia.studioEcomBefore.alt,
      afterSrc: editorialRetouchingMedia.studioEcomAfter.src,
      afterAlt: editorialRetouchingMedia.studioEcomAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: editorialRetouchingMedia.studioEcomAfter.width,
      height: editorialRetouchingMedia.studioEcomAfter.height,
      layout: "cards",
      textPosition: "left",
    },
    {
      sectionId: "editorial-retouching-loungewear-drape-comparison",
      heading: "Loungewear lifestyle & background retouching",
      description:
        "Refine delicate loungewear, satin fabrics, and relaxed tailoring by smoothing unwanted tension creases, balancing waistband alignment, and preserving natural fabric weave.",
      bullets: [
        "Smooth distracting fabric wrinkles while retaining genuine textile texture",
        "Align waistband, pocket drape, and garment silhouette cleanly",
        "Deliver consistent catalog-grade e-commerce and lookbook assets",
      ],
      beforeSrc: editorialRetouchingMedia.loungewearPantsBefore.src,
      beforeAlt: editorialRetouchingMedia.loungewearPantsBefore.alt,
      afterSrc: editorialRetouchingMedia.loungewearPantsAfter.src,
      afterAlt: editorialRetouchingMedia.loungewearPantsAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: editorialRetouchingMedia.loungewearPantsAfter.width,
      height: editorialRetouchingMedia.loungewearPantsAfter.height,
      layout: "cards",
      textPosition: "right",
    },
  ],
  productTypes: [
    {
      id: "spotlight-portrait",
      code: "01",
      title: "Spotlight Portraits",
      subtitle: "Balance dramatic studio lighting while keeping the portrait natural.",
      src: "/images/editorial-retouching/editorial-retouching-product-spotlight-portrait.webp",
      alt: "Fashion portrait finished under a dramatic studio spotlight",
      fit: "cover",
    },
    {
      id: "editorial-makeup",
      code: "02",
      title: "Editorial Makeup",
      subtitle: "Refine eye makeup, lashes, and skin detail for beauty campaigns.",
      src: "/images/editorial-retouching/editorial-retouching-product-editorial-glam-makeup.webp",
      alt: "Close-up editorial makeup portrait with refined lashes and eye detail",
      fit: "cover",
    },
    {
      id: "beauty-portrait",
      code: "03",
      title: "Beauty Portraits",
      subtitle: "Preserve natural skin texture across close-up beauty imagery.",
      src: "/images/editorial-retouching/editorial-retouching-product-skincare-beauty-portrait.webp",
      alt: "Two close-up beauty portraits with polished skin and makeup",
      fit: "cover",
    },
    {
      id: "fashion-editorial",
      code: "04",
      title: "Fashion Editorials",
      subtitle: "Shape campaign color and atmosphere around expressive styling.",
      src: "/images/editorial-retouching/editorial-retouching-product-fashion-fedora-fur-jacket.webp",
      alt: "Fashion model in a fedora and fur jacket reading a newspaper",
      fit: "cover",
    },
    {
      id: "skincare-campaign",
      code: "05",
      title: "Skincare Campaigns",
      subtitle: "Retain realistic skin and product texture in beauty campaigns.",
      src: "/images/editorial-retouching/editorial-retouching-product-skincare-facial-cream.webp",
      alt: "Close-up skincare campaign portrait with cream applied to the face",
      fit: "cover",
    },
    {
      id: "executive-headshot",
      code: "06",
      title: "Executive Headshots",
      subtitle: "Polish professional portraits with balanced tone and clean detail.",
      src: "/images/editorial-retouching/editorial-retouching-product-corporate-executive-male.webp",
      alt: "Retouched corporate headshot of a male executive in a dark jacket",
      fit: "cover",
    },
  ],
  features: {
    heading: "The hallmarks of our editorial craft",
    items: [
      {
        icon: "Wand2",
        title: "100% Manual Dodge & Burn",
        description:
          "We use curve adjustment layers to paint micro-luminance variations by hand, creating natural 3D depth without skin flattening.",
      },
      {
        icon: "Eye",
        title: "Pore-Level Texture Preservation",
        description:
          "We never use generic blur plugins. Every single pore, skin highlight, and subtle texture variation remains crisp and tactile.",
      },
      {
        icon: "Layers",
        title: "16-Bit Non-Destructive Layer Stacks",
        description:
          "All retouching is organized on clearly labeled layers in 16-bit depth (Color, Dodge/Burn, Texture, Final Grade) for full auditability.",
      },
      {
        icon: "Sparkles",
        title: "Hair & Makeup Refinement",
        description:
          "We clean up rogue flyaway hairs, even out makeup blending, clean lipstick bleed, and enhance eye reflections with fine-art precision.",
      },
      {
        icon: "Palette",
        title: "Calibrated Color Matching",
        description:
          "Our EIZO-calibrated displays ensure accurate skintone reproduction across sRGB, Adobe RGB, Display P3, and CMYK press profiles.",
      },
      {
        icon: "Lock",
        title: "Strict NDA & Asset Security",
        description:
          "Unreleased lookbooks and embargoed campaign files are protected by end-to-end encryption and air-gapped storage protocols.",
      },
    ],
  },
  faqs: [
    {
      question: "What is your philosophy on skin retouching?",
      answer:
        "We believe authentic skin texture is the hallmark of luxury photography. We never blur skin or use automated smoothing algorithms. Instead, we use micro dodge & burn and frequency separation to remove temporary blemishes while preserving every natural pore, freckle, and skin contour.",
    },
    {
      question: "Which file formats should I send?",
      answer:
        "For best editorial results, send uncompressed 16-bit RAW files (CR3, NEF, ARW, DNG) or 16-bit PSD/TIFF files. This gives our colorists maximum dynamic range for shadow recovery and color grading.",
    },
    {
      question: "Can you match the mood of a specific visual reference?",
      answer:
        "Yes. You can provide moodboards, lighting references, or tear sheets, and our team will match the grain, contrast, color temperature, and tonal palette of your creative direction.",
    },
    {
      question: "What is the turnaround time for an editorial story?",
      answer:
        "Editorial stories (6 to 20 frames) typically require 24 to 48 hours for first proofs. We offer expedited 24-hour turnaround for urgent press deadlines.",
    },
    {
      question: "Do you offer test edits on our RAW files?",
      answer:
        "Yes, we offer a complimentary test edit on 1 to 2 RAW files so you can review our skin texture fidelity and color styling before booking your project.",
    },
  ],
  cta: {
    heading: "Prepare your next editorial campaign",
    description:
      "Send us your raw files and moodboard. Our senior retouchers will produce a complimentary test grade tailored to your art direction.",
    steps: [
      {
        title: "Send RAW files & reference",
        description:
          "Upload your 16-bit raw captures with your lighting and mood notes.",
      },
      {
        title: "Review high-res proofs",
        description:
          "Inspect our skin texture preservation and dodge & burn sculpting at 100% zoom.",
      },
      {
        title: "Publish cover-ready assets",
        description:
          "Receive layered 16-bit PSDs and calibrated output files ready for press.",
      },
    ],
  },
};
